import { Icon } from '@speaking-coach/ui'

import { formatBusinessDate } from '../common/access-window'
import type { CohortRow } from '../cohorts/types'

type InvitationScheduleSource =
  | { kind: 'cohort'; cohort: CohortRow }
  | { kind: 'private' }
  | { kind: 'unselected' }

type InvitationScheduleCopy = {
  before: string
  sendAt?: string
  after?: string
}

/** `2026-09-01` + `-7` → `2026-08-25`. Pure business-date arithmetic. */
function addDays(dateIso: string, days: number): string {
  const at = new Date(`${dateIso}T00:00:00Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return at.toISOString().slice(0, 10)
}

function invitationScheduleCopy(
  source: InvitationScheduleSource,
): InvitationScheduleCopy {
  if (source.kind === 'unselected') {
    return { before: 'Choose a cohort to see its invitation timing.' }
  }
  if (source.kind === 'private') {
    return {
      before:
        'The invitation is queued when this enrollment is created and goes out within the hour.',
    }
  }

  const { cohort } = source
  if (cohort.inviteSendAt !== null) {
    return {
      before: 'Invites go out ',
      sendAt: new Intl.DateTimeFormat('en', {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: 'Asia/Tokyo',
      }).format(new Date(cohort.inviteSendAt)),
      after: ' (Japan time).',
    }
  }
  if (
    cohort.inviteSendOffsetDays !== null &&
    cohort.scheduledStartOn !== null
  ) {
    const onDate = addDays(
      cohort.scheduledStartOn,
      cohort.inviteSendOffsetDays,
    )
    const distance = Math.abs(cohort.inviteSendOffsetDays)
    const relative =
      cohort.inviteSendOffsetDays < 0
        ? 'before'
        : cohort.inviteSendOffsetDays > 0
          ? 'after'
          : 'on'
    const relation =
      relative === 'on'
        ? 'on the start date'
        : `${distance} ${distance === 1 ? 'day' : 'days'} ${relative} the start`
    return {
      before: 'Invites go out ',
      sendAt: `${formatBusinessDate(onDate)} at 09:00 Japan time`,
      after: ` (${relation}).`,
    }
  }
  return {
    before:
      'This cohort has no send schedule — invites go out within the hour of creating.',
  }
}

/** The invitation timing shown before an operator creates an enrollment. */
export function invitationScheduleLabel(source: InvitationScheduleSource): string {
  const { before, sendAt = '', after = '' } = invitationScheduleCopy(source)
  return `${before}${sendAt}${after}`
}

/** Shared footer for the Program access window on enrollment-create forms. */
export function InvitationScheduleSummary({
  source,
}: {
  source: InvitationScheduleSource
}): JSX.Element {
  const missingCohortSchedule =
    source.kind === 'cohort' &&
    source.cohort.inviteSendAt === null &&
    (source.cohort.inviteSendOffsetDays === null ||
      source.cohort.scheduledStartOn === null)
  const copy = invitationScheduleCopy(source)

  return (
    <div
      aria-label="Invitation email schedule"
      className="mt-3 flex items-start gap-2 border-t border-border-default pt-3 text-caption"
    >
      <Icon
        name="schedule"
        size={14}
        className="mt-0.5 shrink-0 text-text-secondary"
      />
      <div>
        <p className="font-medium text-text-primary">Invitation email</p>
        <p className="mt-0.5 flex items-start gap-1.5 text-text-secondary">
          {missingCohortSchedule && (
            <Icon
              name="warning"
              size={14}
              data-testid="missing-invite-schedule-warning"
              className="mt-0.5 shrink-0 text-text-secondary"
            />
          )}
          <span>
            {copy.before}
            {copy.sendAt && (
              <strong className="font-medium text-text-primary">
                {copy.sendAt}
              </strong>
            )}
            {copy.after}
          </span>
        </p>
      </div>
    </div>
  )
}
